import { useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const NAME_DISALLOWED_CHARS = /[^A-Za-zÇĞİÖŞÜçğıöşü\s]/g;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

export const isNameValid = (name) => name.trim().length >= 2 && !/\d/.test(name);
export const isEmailValid = (email) => {
  const trimmed = (email || "").trim();
  if (!trimmed) return true; // e-posta artık opsiyonel; boşsa geçerli sayılır
  return EMAIL_REGEX.test(trimmed);
};
export const isPhoneValid = (phone) => Boolean(phone) && isValidPhoneNumber(phone);
export const isContactStepValid = (form) => isNameValid(form.name) && isPhoneValid(form.phone) && isEmailValid(form.email);

/**
 * Step 1: basic contact details (name / phone / email).
 * - Name: letters only (digits are stripped as the user types). Required.
 * - Phone: international input with a country-code selector; validated for real,
 *   plausible numbers via libphonenumber-js (through react-phone-number-input). Required.
 * - Email: OPTIONAL. If left empty, no error is shown and the user may proceed.
 *   If the user types something, it must match a real email format, otherwise an
 *   inline error is shown and proceeding is blocked.
 */
const ContactStep = ({ form, onChange }) => {
  const [touched, setTouched] = useState({});
  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="inquiry-name">
          Ad Soyad <span className="text-[color:var(--brand-gold-2)]">*</span>
        </Label>
        <Input
          id="inquiry-name"
          data-testid="inquiry-name-input"
          value={form.name}
          onChange={(e) => onChange("name", e.target.value.replace(NAME_DISALLOWED_CHARS, ""))}
          onBlur={() => markTouched("name")}
          placeholder="Adınız Soyadınız"
          aria-invalid={touched.name && !isNameValid(form.name)}
        />
        {touched.name && !isNameValid(form.name) && (
          <p className="text-xs text-[color:hsl(var(--destructive))]" data-testid="inquiry-name-error">
            Lütfen adınızı ve soyadınızı girin (sadece harf kullanılabilir).
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="inquiry-phone">
          Telefon <span className="text-[color:var(--brand-gold-2)]">*</span>
        </Label>
        <PhoneInput
          international
          defaultCountry="TR"
          countryCallingCodeEditable={false}
          value={form.phone}
          onChange={(v) => onChange("phone", v || "")}
          onBlur={() => markTouched("phone")}
          placeholder="5xx xxx xx xx"
          numberInputProps={{ id: "inquiry-phone", "data-testid": "inquiry-phone-input" }}
          className={`karsli-phone-input ${touched.phone && !isPhoneValid(form.phone) ? "karsli-phone-input--invalid" : ""}`}
        />
        {touched.phone && !isPhoneValid(form.phone) && (
          <p className="text-xs text-[color:hsl(var(--destructive))]" data-testid="inquiry-phone-error">
            Lütfen ülke kodunu seçip geçerli bir telefon numarası girin.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="inquiry-email">E-posta</Label>
        <Input
          id="inquiry-email"
          type="email"
          data-testid="inquiry-email-input"
          value={form.email}
          onChange={(e) => onChange("email", e.target.value)}
          onBlur={() => markTouched("email")}
          placeholder="ornek@eposta.com"
          aria-invalid={touched.email && !isEmailValid(form.email)}
        />
        {touched.email && !isEmailValid(form.email) && (
          <p className="text-xs text-[color:hsl(var(--destructive))]" data-testid="inquiry-email-error">
            Lütfen geçerli bir e-posta adresi girin.
          </p>
        )}
      </div>
    </div>
  );
};

export default ContactStep;
